/**
 * VimsottariDashaTable Component
 * Drill-down accordion table for Vimsottari Dasha periods
 * Uses Chakra UI Table for proper layout with horizontal scroll in narrow mode
 */

import React from 'react';
import {
  IconButton,
  Tooltip,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  useDisclosure,
  Badge,
  Wrap,
  WrapItem,
  Box,
  Text,
  Input,
  HStack,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer
} from '@chakra-ui/react';
import { ChevronDownIcon } from '@chakra-ui/icons';
import { Target } from 'lucide-react';
import { JatakaResponse } from '../../../../../../models/api';
import { ExpandIcon } from '../../ExpandIcon';
import { useVimsottariDasha } from '../../hooks/useVimsottariDasha';
import {
  DashaPeriod,
  getLevelName,
  getLevelAbbrev,
  DashaStartingPoint,
  DashaYearType,
  DashaCustomStartPoint,
  DASHA_STARTING_POINTS,
  DASHA_STARTING_POINT_INFO,
  DASHA_YEAR_TYPES,
  DASHA_YEAR_TYPE_INFO
} from '../../data/VimsottariDashaData';
import { getSavedDateTimeFormat, formatDateTime } from '../../../../../../config/dateTimeFormats';
import {
  TABLE_CONTAINER_STYLES,
  TABLE_TH_BASE_STYLES,
  TABLE_TD_BASE_STYLES
} from '../styles/tableStyles';

/**
 * Get pattern with seconds for Dasha dates
 * Adds :ss after mm in the pattern
 */
const getPatternWithSeconds = (pattern: string): string => {
  // Add seconds after minutes: HH:mm -> HH:mm:ss, h:mm A -> h:mm:ss A
  return pattern.replace(/mm(?!\s*A)/, 'mm:ss').replace(/mm(\s*A)/, 'mm:ss$1');
};

// Planet colors (Sanskrit names) - matching existing planet styling
const PLANET_COLORS: Record<string, string> = {
  Surya: '#DD6B20',
  Chandra: '#718096',
  Mangala: '#E53E3E',
  Rahu: '#805AD5',
  Guru: '#D69E2E',
  Shani: '#2B6CB0',
  Budha: '#38A169',
  Ketu: '#B794F4',
  Shukra: '#00B5D8',
};

/**
 * Starting Point Selector - inline popover for Dasha starting point
 */
interface StartingPointSelectorProps {
  value: DashaStartingPoint;
  onChange: (point: DashaStartingPoint) => void;
  customSettings: DashaCustomStartPoint;
  onCustomSettingsChange: (settings: DashaCustomStartPoint) => void;
  isLoading?: boolean;
}

const StartingPointSelector: React.FC<StartingPointSelectorProps> = ({
  value,
  onChange,
  customSettings,
  onCustomSettingsChange,
  isLoading
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const info = DASHA_STARTING_POINT_INFO[value];

  // Local state for custom input
  const [customDegrees, setCustomDegrees] = React.useState(customSettings.degrees.toString());
  const [customLabel, setCustomLabel] = React.useState(customSettings.label);
  const [showCustomInput, setShowCustomInput] = React.useState(false);

  // Sync local state when props change
  React.useEffect(() => {
    setCustomDegrees(customSettings.degrees.toString());
    setCustomLabel(customSettings.label);
  }, [customSettings.degrees, customSettings.label]);

  const handleSelect = (point: DashaStartingPoint) => {
    if (point === 'Custom') {
      setShowCustomInput(true);
      return;
    }

    if (point !== value) {
      setShowCustomInput(false);
      onChange(point);
    }
    onClose();
  };

  const handleCustomApply = () => {
    const degrees = parseFloat(customDegrees) || 0;
    const validDegrees = Math.max(0, Math.min(360, degrees));
    onCustomSettingsChange({ degrees: validDegrees, label: customLabel || 'Cu' });
    setShowCustomInput(false);
    onClose();
  };

  // Display name for trigger
  const displayName = value === 'Custom'
    ? `${customSettings.label}[${customSettings.degrees.toFixed(1)}°]`
    : info.label;

  return (
    <Popover isOpen={isOpen} onOpen={onOpen} onClose={onClose} placement="bottom-start">
      <PopoverTrigger>
        <Box
          as="button"
          display="flex"
          alignItems="center"
          gap={0.5}
          px={1}
          py={0.5}
          borderRadius="sm"
          bg="transparent"
          cursor={isLoading ? 'wait' : 'pointer'}
          opacity={isLoading ? 0.6 : 1}
          _hover={{ bg: 'gray.100' }}
          transition="background 0.2s"
        >
          <Text fontSize="xs" fontWeight="medium" color={value === 'Custom' ? 'blue.600' : 'gray.600'}>
            {displayName}
          </Text>
          <ChevronDownIcon boxSize={3} color="gray.400" />
        </Box>
      </PopoverTrigger>
      <PopoverContent width="auto" maxW="320px" _focus={{ boxShadow: 'sm' }}>
        <PopoverBody p={2}>
          <Wrap spacing={1}>
            {DASHA_STARTING_POINTS.map(point => {
              const pointInfo = DASHA_STARTING_POINT_INFO[point];
              const isActive = point === value;
              return (
                <WrapItem key={point}>
                  <Badge
                    colorScheme={isActive ? (point === 'Custom' ? 'blue' : 'purple') : 'gray'}
                    variant={isActive ? 'solid' : 'outline'}
                    fontSize="xs"
                    px={1.5}
                    py={0.5}
                    borderRadius="sm"
                    cursor="pointer"
                    onClick={() => handleSelect(point)}
                    _hover={{ bg: isActive ? undefined : 'gray.100', transform: 'scale(1.02)' }}
                    transition="all 0.15s"
                    fontWeight={isActive ? 'semibold' : 'normal'}
                  >
                    {pointInfo.label}
                  </Badge>
                </WrapItem>
              );
            })}
          </Wrap>

          {/* Custom input fields */}
          {showCustomInput && (
            <Box mt={2} pt={2} borderTop="1px solid" borderColor="gray.200">
              <HStack spacing={1} mb={1}>
                <Input
                  size="xs"
                  width="70px"
                  placeholder="0-360"
                  value={customDegrees}
                  onChange={(e) => setCustomDegrees(e.target.value)}
                  type="number"
                  step="0.1"
                  min="0"
                  max="360"
                />
                <Text fontSize="xs" color="gray.500">°</Text>
                <Input
                  size="xs"
                  width="45px"
                  placeholder="Cu"
                  value={customLabel}
                  onChange={(e) => setCustomLabel(e.target.value.slice(0, 2))}
                  maxLength={2}
                />
                <Button size="xs" colorScheme="blue" onClick={handleCustomApply} isLoading={isLoading}>
                  OK
                </Button>
              </HStack>
            </Box>
          )}

          {isLoading && !showCustomInput && (
            <Text fontSize="2xs" color="gray.500" mt={1}>
              Updating...
            </Text>
          )}
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};

/**
 * Year Type Selector - inline popover for calculation method
 */
interface YearTypeSelectorProps {
  value: DashaYearType;
  onChange: (type: DashaYearType) => void;
  customYearDays: number | null;
  onCustomYearDaysChange: (days: number) => void;
  isLoading?: boolean;
}

const YearTypeSelector: React.FC<YearTypeSelectorProps> = ({
  value,
  onChange,
  customYearDays,
  onCustomYearDaysChange,
  isLoading
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const info = DASHA_YEAR_TYPE_INFO[value];

  // Local state for custom input
  const [customDays, setCustomDays] = React.useState((customYearDays || 365).toString());
  const [showCustomInput, setShowCustomInput] = React.useState(false);

  // Sync local state when props change
  React.useEffect(() => {
    if (customYearDays !== null) {
      setCustomDays(customYearDays.toString());
    }
  }, [customYearDays]);

  const handleSelect = (type: DashaYearType) => {
    if (type === 'user_defined') {
      setShowCustomInput(true);
      return;
    }

    if (type !== value) {
      setShowCustomInput(false);
      onChange(type);
    }
    onClose();
  };

  const handleCustomApply = () => {
    const days = parseFloat(customDays) || 365;
    const validDays = Math.max(1, Math.min(400, days));
    onCustomYearDaysChange(validDays);
    setShowCustomInput(false);
    onClose();
  };

  // Get label for display - use full names from info
  const getLabel = (type: DashaYearType): string => {
    return DASHA_YEAR_TYPE_INFO[type].label;
  };

  // Format days with up to 5 decimal places, trimming trailing zeros
  const formatDays = (days: number): string => {
    const formatted = days.toFixed(5);
    return formatted.replace(/\.?0+$/, '');
  };

  // Display for trigger when user_defined is active
  const displayLabel = value === 'user_defined' && customYearDays
    ? `Custom (${formatDays(customYearDays)}d)`
    : getLabel(value);

  // Days display
  const daysDisplay = value === 'user_defined' && customYearDays
    ? `${formatDays(customYearDays)} days`
    : `${info.days} days`;

  return (
    <>
      <Popover isOpen={isOpen} onOpen={onOpen} onClose={onClose} placement="bottom-start">
        <PopoverTrigger>
          <Box
            as="button"
            display="flex"
            alignItems="center"
            gap={0.5}
            px={1}
            py={0.5}
            borderRadius="sm"
            bg="transparent"
            cursor={isLoading ? 'wait' : 'pointer'}
            opacity={isLoading ? 0.6 : 1}
            _hover={{ bg: 'gray.100' }}
            transition="background 0.2s"
          >
            <Text fontSize="xs" fontWeight="medium" color={value === 'user_defined' ? 'blue.600' : 'gray.600'}>
              {displayLabel}
            </Text>
            <ChevronDownIcon boxSize={3} color="gray.400" />
          </Box>
        </PopoverTrigger>
        <PopoverContent width="auto" maxW="320px" _focus={{ boxShadow: 'sm' }}>
          <PopoverBody p={2}>
            <Wrap spacing={1}>
              {DASHA_YEAR_TYPES.map(type => {
                const typeInfo = DASHA_YEAR_TYPE_INFO[type];
                const isActive = type === value;
                return (
                  <WrapItem key={type}>
                    <Tooltip label={`${typeInfo.days} days - ${typeInfo.description}`} placement="top">
                      <Badge
                        colorScheme={isActive ? 'blue' : 'gray'}
                        variant={isActive ? 'solid' : 'outline'}
                        fontSize="xs"
                        px={1.5}
                        py={0.5}
                        borderRadius="sm"
                        cursor="pointer"
                        onClick={() => handleSelect(type)}
                        _hover={{ bg: isActive ? undefined : 'gray.100', transform: 'scale(1.02)' }}
                        transition="all 0.15s"
                        fontWeight={isActive ? 'semibold' : 'normal'}
                      >
                        {getLabel(type)}
                      </Badge>
                    </Tooltip>
                  </WrapItem>
                );
              })}
            </Wrap>

            {/* Custom input fields */}
            {showCustomInput && (
              <Box mt={2} pt={2} borderTop="1px solid" borderColor="gray.200">
                <HStack spacing={1} mb={1}>
                  <Input
                    size="xs"
                    width="90px"
                    placeholder="365.25636"
                    value={customDays}
                    onChange={(e) => setCustomDays(e.target.value)}
                    type="number"
                    step="0.00001"
                    min="1"
                    max="400"
                  />
                  <Text fontSize="xs" color="gray.500">days</Text>
                  <Button size="xs" colorScheme="blue" onClick={handleCustomApply} isLoading={isLoading}>
                    OK
                  </Button>
                </HStack>
              </Box>
            )}

            {isLoading && !showCustomInput && (
              <Text fontSize="2xs" color="gray.500" mt={1}>
                Updating...
              </Text>
            )}
          </PopoverBody>
        </PopoverContent>
      </Popover>
      {/* Info line below showing days and description */}
      <Text fontSize="10px" color="gray.500" ml={1}>
        ({daysDisplay})
      </Text>
    </>
  );
};

// Column configuration for Dasha table
const DASHA_COLUMN_CONFIG = {
  expand: { w: '24px', minW: '24px' },
  planet: { minW: '70px' },
  level: { minW: '32px', w: '32px' },
  start: { minW: '140px' },
  end: { minW: '140px' },
};

// Dasha table styles
const DASHA_TABLE_STYLES = {
  planetCell: (color: string, isHeader: boolean) => ({
    color,
    fontWeight: isHeader ? 600 : 500,
  }),
  levelCell: {
    color: '#666',
    fontWeight: 500,
  },
  dateCell: {
    color: '#444',
    fontWeight: 500,
  },
  currentRowBg: 'rgba(72, 187, 120, 0.1)',
};

interface DashaTableRowProps {
  period: DashaPeriod;
  isAncestor?: boolean;
  canExpand: boolean;
  onExpand: () => void;
  onCollapse: () => void;
  nestingLevel?: number;
}

/**
 * Single dasha row component - uses Chakra UI Table
 */
const DashaTableRow: React.FC<DashaTableRowProps> = ({
  period,
  isAncestor = false,
  canExpand,
  onExpand,
  onCollapse,
  nestingLevel = 0
}) => {
  const format = getSavedDateTimeFormat();
  const patternWithSeconds = getPatternWithSeconds(format.pattern);
  const planet = period.planets[period.planets.length - 1];
  const levelAbbrev = getLevelAbbrev(period.level);
  const planetColor = PLANET_COLORS[planet] || '#681515';

  const startDateStr = formatDateTime(period.startDate, patternWithSeconds);
  const endDateStr = formatDateTime(period.endDate, patternWithSeconds);

  const handleClick = () => {
    if (isAncestor) {
      onCollapse();
    } else if (canExpand) {
      onExpand();
    }
  };

  const isClickable = canExpand || isAncestor;
  const indentPx = nestingLevel * 8;

  return (
    <Tr
      onClick={isClickable ? handleClick : undefined}
      cursor={isClickable ? 'pointer' : 'default'}
      _hover={isClickable ? { bg: '#f8f9fa' } : undefined}
      bg={period.isCurrent ? DASHA_TABLE_STYLES.currentRowBg : isAncestor ? '#f8f9fa' : 'white'}
      transition="background 0.15s"
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onKeyDown={(e) => {
        if ((e.key === 'Enter' || e.key === ' ') && isClickable) {
          e.preventDefault();
          handleClick();
        }
      }}
      className="dasha-table-row"
    >
      <Td
        {...TABLE_TD_BASE_STYLES}
        w={DASHA_COLUMN_CONFIG.expand.w}
        minW={DASHA_COLUMN_CONFIG.expand.minW}
        p={1}
        pl={`${8 + indentPx}px`}
      >
        {isClickable ? (
          <ExpandIcon isExpanded={isAncestor} size={12} />
        ) : (
          <Box w="14px" display="inline-block" />
        )}
      </Td>
      <Td
        {...TABLE_TD_BASE_STYLES}
        minW={DASHA_COLUMN_CONFIG.planet.minW}
        sx={DASHA_TABLE_STYLES.planetCell(planetColor, isAncestor)}
        className="dasha-planet-cell"
      >
        {planet}
      </Td>
      <Td
        {...TABLE_TD_BASE_STYLES}
        w={DASHA_COLUMN_CONFIG.level.w}
        minW={DASHA_COLUMN_CONFIG.level.minW}
        sx={DASHA_TABLE_STYLES.levelCell}
        className="dasha-level-cell"
      >
        {levelAbbrev}
      </Td>
      <Td
        {...TABLE_TD_BASE_STYLES}
        minW={DASHA_COLUMN_CONFIG.start.minW}
        sx={DASHA_TABLE_STYLES.dateCell}
        className="dasha-date-cell"
      >
        {startDateStr}
      </Td>
      <Td
        {...TABLE_TD_BASE_STYLES}
        minW={DASHA_COLUMN_CONFIG.end.minW}
        sx={DASHA_TABLE_STYLES.dateCell}
        className="dasha-date-cell"
      >
        {endDateStr}
      </Td>
    </Tr>
  );
};


export interface VimsottariDashaTableProps {
  apiResponse?: JatakaResponse | null;
  /** Called when section is expanded - triggers data load */
  onSectionExpand?: () => void;
}

/**
 * Main Vimsottari Dasha table with drill-down accordion
 */
export const VimsottariDashaTable: React.FC<VimsottariDashaTableProps> = ({
  apiResponse,
  onSectionExpand
}) => {
  const {
    loading,
    error,
    isLoaded,
    visiblePeriods,
    ancestorPeriods,
    expand,
    collapse,
    goToCurrent,
    currentLevel,
    canExpandDeeper,
    loadData,
    startingPoint,
    setStartingPoint,
    yearType,
    setYearType,
    customYearDays,
    setCustomYearDaysAndFetch,
    customStartPoint,
    setCustomStartPoint
  } = useVimsottariDasha(apiResponse);

  // Load data when component mounts (section is expanded)
  React.useEffect(() => {
    if (!isLoaded && !loading) {
      loadData();
    }
  }, [isLoaded, loading, loadData]);

  if (loading) {
    return (
      <div style={{ padding: '8px 0', color: '#718096', fontSize: '13px' }}>
        Loading Vimsottari Dasha...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '8px 0', color: '#E53E3E', fontSize: '13px' }}>
        {error}
      </div>
    );
  }

  if (!isLoaded || visiblePeriods.length === 0) {
    return (
      <div style={{ padding: '8px 0', color: '#718096', fontSize: '13px' }}>
        No Dasha data available
      </div>
    );
  }

  return (
    <Box className="dasha-table-container">
      {/* Settings row with selectors */}
      <HStack
        spacing={2}
        mb={2}
        pb={2}
        borderBottom="1px solid"
        borderColor="gray.200"
        flexWrap="wrap"
        className="dasha-settings-row"
      >
        <Text fontSize="xs" fontWeight={500} color="gray.500">
          Starting:
        </Text>
        <StartingPointSelector
          value={startingPoint}
          onChange={setStartingPoint}
          customSettings={customStartPoint}
          onCustomSettingsChange={setCustomStartPoint}
          isLoading={loading}
        />
        <Text fontSize="xs" fontWeight={500} color="gray.500" ml={2}>
          Method:
        </Text>
        <YearTypeSelector
          value={yearType}
          onChange={setYearType}
          customYearDays={customYearDays}
          onCustomYearDaysChange={setCustomYearDaysAndFetch}
          isLoading={loading}
        />
      </HStack>

      {/* Header with level name and Go to Current button */}
      <HStack justify="space-between" mb={2} pr={1}>
        <Text fontSize="xs" fontWeight={500} color="gray.500" className="dasha-level-title">
          {ancestorPeriods.length === 0
            ? 'Mahadashas'
            : `${getLevelName(currentLevel)}s in this ${getLevelName(currentLevel - 1)}`
          }
        </Text>
        <Tooltip label="Go to current period" placement="top">
          <IconButton
            aria-label="Go to current period"
            icon={<Target size={14} />}
            size="xs"
            variant="ghost"
            onClick={goToCurrent}
            minW="24px"
            h="24px"
          />
        </Tooltip>
      </HStack>

      {/* Table with horizontal scroll */}
      <TableContainer
        {...TABLE_CONTAINER_STYLES}
        border="1px solid #e5e5e5"
        borderRadius="2px"
      >
        <Table size="sm" variant="simple" minW="400px" className="dasha-table">
          <Thead bg="#f8f9fa">
            <Tr>
              <Th {...TABLE_TH_BASE_STYLES} w={DASHA_COLUMN_CONFIG.expand.w} p={1} borderColor="#e5e5e5"></Th>
              <Th {...TABLE_TH_BASE_STYLES} minW={DASHA_COLUMN_CONFIG.planet.minW} className="dasha-header-cell">Planet</Th>
              <Th {...TABLE_TH_BASE_STYLES} w={DASHA_COLUMN_CONFIG.level.w} className="dasha-header-cell">Lvl</Th>
              <Th {...TABLE_TH_BASE_STYLES} minW={DASHA_COLUMN_CONFIG.start.minW} className="dasha-header-cell">Start</Th>
              <Th {...TABLE_TH_BASE_STYLES} minW={DASHA_COLUMN_CONFIG.end.minW} className="dasha-header-cell">End</Th>
            </Tr>
          </Thead>
          <Tbody>
            {/* Ancestor chain (expanded headers) */}
            {ancestorPeriods.map((period, idx) => (
              <DashaTableRow
                key={`ancestor-${idx}`}
                period={period}
                isAncestor={true}
                canExpand={false}
                onExpand={() => {}}
                onCollapse={() => collapse(idx + 1)}
                nestingLevel={idx}
              />
            ))}

            {/* Visible periods (children of current level) */}
            {visiblePeriods.map((period, idx) => {
              const planet = period.planets[period.planets.length - 1];

              return (
                <DashaTableRow
                  key={`period-${idx}-${planet}`}
                  period={period}
                  isAncestor={false}
                  canExpand={canExpandDeeper}
                  onExpand={() => expand(planet, currentLevel)}
                  onCollapse={() => {}}
                  nestingLevel={ancestorPeriods.length}
                />
              );
            })}
          </Tbody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default VimsottariDashaTable;
